import { useEffect, useMemo, useState } from "react";
import XMLViewer from "react-xml-viewer";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import { getRootNameFromXML, isWellFormedXML, sanitizeXML } from "../../../shared/utility/xml.utility";



export default function XmlPrettyViewer({
    dataString = "",
    title = "XML",
    collapsed, // NOTE: true => OPEN ALL (root + children), false/undefined => collapse all
    invalidFallback = "No valid XML to display. Click exapnd all to see details",
    showErrorBox = true,
    onCollapsedChange,
}) {
    const cleaned = useMemo(() => sanitizeXML(dataString), [dataString]);
    const valid = useMemo(() => isWellFormedXML(cleaned), [cleaned]);
    const rootName = useMemo(() => getRootNameFromXML(cleaned), [cleaned]);

    // Interpret prop: true => OPEN ALL
    const openAll = collapsed ? false : true;

    // Root open mirrors prop (true => open root)
    const [rootOpen, setRootOpen] = useState(openAll);
    useEffect(() => setRootOpen(openAll), [openAll]);

    // Reusable copy hook
    const { copied, copy, ManualCopyUI } = useCopyToClipboard(cleaned || dataString);

    const handleDetailsToggle = (e) => {
        const open = e.currentTarget.open;
        setRootOpen(open);
        // Map back to prop semantics: open => collapsed=false, closed => collapsed=true
        onCollapsedChange?.(!open ? false : true);
    };

    return (
        <div className="flex flex-col">
            <div className="flex flex-row items-center gap-2 mb-2">
                <h1 style={{ fontSize: "1.3rem" }}>{title}</h1>
                <button
                    onClick={copy}
                    className="btn"
                    style={{
                        background: "#1e4e9c",
                        color: "white",
                        border: "none",
                        borderRadius: "5px",
                        padding: "5px 5px",
                        cursor: "pointer",
                        height: "2.5rem",
                    }}
                >
                    {copied ? "Copied!" : "Copy"}
                </button>
            </div>

            {!valid ? (
                <div
                    style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        padding: "10px 12px",
                        background: "#fafafa",
                        color: "#374151",
                    }}
                >
                    {invalidFallback}
                    {showErrorBox && openAll && (
                        <pre
                            style={{
                                marginTop: 8,
                                whiteSpace: "pre-wrap",
                                fontFamily: "monospace",
                                fontSize: 12,
                                color: "#6b7280",
                            }}
                        >
                            {dataString}
                        </pre>
                    )}
                </div>
            ) : (
                // Root: uses <details> so it *can* be closed; react-xml-viewer can’t close root itself.
                <details open={rootOpen} onToggle={handleDetailsToggle}>
                    <summary style={{ cursor: "pointer", userSelect: "none", color: '#d43900' }}>
                        &lt;{rootName}&gt;…&lt;/{rootName}&gt;
                        {/* <label style={{ color: 'black', cursor: 'pointer' }} className="ml-2">({rootOpen ? "click to collapse" : "click to expand"})</label> */}
                    </summary>

                    <div style={{ marginTop: 8 }}>
                        {/* Children: openAll => expand all (collapsed=false); else collapse all (true) */}
                        <XMLViewer xml={cleaned} collapsible={true} collapsed={!openAll} />
                    </div>
                    {/* Manual fallback UI if needed */}
                    {ManualCopyUI}
                </details>
            )}
        </div>
    );
}
